import { useMemo } from 'react';
import { useField, FieldArray, useFormikContext } from 'formik';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { getJobCategory } from 'pages/Bridges/ManagerValidation/_api';
import AppWrappedInput from '../../AppWrappedInput';
import RequiredIndicator from '../../RequiredIndicator';
import {
	EPIC_APP_ID,
	MAX_COUNTS,
} from '../_const';
import { HP_APP_ID } from 'pages/ApplicationRequest/components/containers/1696EpicHP/_const';
import { nanoid } from 'nanoid';
import JobCategorySearch from './JobCategorySearch';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		color: theme.palette.primary.dark,
	},
}));

const JobCategoryTable = ({
	systemID = EPIC_APP_ID,
	setSelection = () => {},
	setOpen = () => {},
	onJobCategoriesChange = () => {},
	...props
}) => {
	const MAX_COUNT = MAX_COUNTS.JOB_CATEGORIES;
	const [field, meta, helpers] = useField(props);
	const { values } = useFormikContext();

	const visibleRows = useMemo(() => {
		// return field.value.filter(jc => !!jc.jobCategoryId);
		return field.value;
	}, [field.value]);

	const showExperience = useMemo(() => systemID !== HP_APP_ID, [systemID]);
	const showWave = useMemo(() => systemID !== HP_APP_ID, [systemID]);

	const headCells = [
		{
			id: 'jobCategoryName',
			numeric: false,
			hasHeaderIcon: false,
			label: 'Name',
		},
		...(showExperience
			? [
					{
						id: 'jobCategoryExperience',
						numeric: false,
						hasHeaderIcon: false,
						label: 'Experience',
					},
				]
			: []),
		...(showWave
			? [
					{
						id: 'wave',
						numeric: false,
						hasHeaderIcon: false,
						label: 'Wave',
					},
				]
			: []),
		{
			id: 'remove',
			numeric: false,
			hasHeaderIcon: false,
			label: '',
		},
	];

	const onHelp = async id => {
		const selectedJobCategory = await getJobCategory(id);
		setSelection(selectedJobCategory);
		setOpen(true);
	};

	/**
	 * Emits job category change context to parent handlers.
	 *
	 * @param {object} payload Change payload.
	 * @param {object=} payload.jobCategory New job category for adding or replacing.
	 * @param {object=} payload.previousJobCategory Previous job category for replacing or removing.
	 * @returns {void}
	 */
	const notifyJobCategoriesChange = payload => {
		onJobCategoriesChange(payload);
	};

	return (
		<AppWrappedInput
			label={
				<>
					<Stack direction='row' gap={1}>
						<RequiredIndicator />
						Job Categories
						<IconButton
							sx={{ padding: '0' }}
							aria-label='Help'
							title='Click here to search and view information about job categories.'
							edge='end'
							size='small'
							onClick={() => setOpen(true)}
						>
							<SearchIcon fontSize='small' />
						</IconButton>
					</Stack>
				</>
			}
			error={
				meta.touched
					? Array.isArray(meta.error)
						? 'Please fill out all information above'
						: meta.error
					: null
			}
		>
			<TableContainer>
				<Table
					sx={{ minWidth: 500, maxWidth: '100%' }}
					aria-labelledby='tableTitle'
					size={'small'}
					stickyHeader
				>
					<TableHead>
						<TableRow>
							{headCells.map(headCell => (
								<StyledTableCell
									key={headCell.id}
									align={headCell.numeric ? 'right' : 'left'}
									padding={
										headCell.hasHeaderIcon
											? 'none'
											: 'normal'
									}
								>
									<Typography
										variant='body1'
										sx={{
											textDecoration: 'underline',
										}}
									>
										{headCell.label}
									</Typography>
								</StyledTableCell>
							))}
						</TableRow>
					</TableHead>
					<FieldArray name={props.name}>
						{({ push, remove, replace }) => (
							<TableBody>
								{visibleRows.length > 0 &&
									visibleRows.map((row, index) => {
										const isInactive =
											row?.active === false;
										return (
											<TableRow
												hover
												role='checkbox'
												tabIndex={-1}
												key={
													row?.jobCategoryId ??
													`job-category-row-${index}`
												}
												sx={
													isInactive
														? {
																opacity: 0.75,
																backgroundColor:
																	'action.disabledBackground',
															}
														: undefined
												}
											>
												<TableCell
													component='th'
													scope='row'
												>
													<JobCategorySearch
														value={row}
														allValues={visibleRows}
														readOnly={isInactive}
														setValue={v => {
															replace(index, v);
															notifyJobCategoriesChange(
																{
																	jobCategory:
																		v,
																	previousJobCategory:
																		row,
																},
															);
														}}
														onBlur={() =>
															helpers.setTouched(
																true,
															)
														}
														onHelp={() =>
															onHelp(
																row.jobCategoryId,
															)
														}
														autoFocus={index !== 0}
														touched={meta.touched}
														error={
															Array.isArray(
																meta.error,
															) &&
															meta.error.length >=
																index
																? meta.error[
																		index
																	]
																		?.jobCategoryName
																: null
														}
														systemID={systemID}
													/>
												</TableCell>
												{showExperience && (
													<TableCell
														sx={{
															width: '20%',
														}}
													>
														<FormControl
															variant='filled'
															fullWidth
														>
															<Select
																variant='standard'
																size={'small'}
																label=''
																disabled={
																	isInactive
																}
																onChange={e => {
																	const newValues =
																		{
																			...row,
																			jobCategoryExperience:
																				e
																					.target
																					.value,
																		};
																	replace(
																		index,
																		newValues,
																	);
																}}
																SelectDisplayProps={{
																	sx: {
																		padding:
																			'2px 0 3px 0',
																	},
																}}
																value={
																	row.jobCategoryExperience
																}
																onBlur={() =>
																	helpers.setTouched(
																		true,
																	)
																}
															>
																<MenuItem
																	value={
																		'New'
																	}
																>
																	New
																</MenuItem>
																<MenuItem
																	value={
																		'Experienced'
																	}
																>
																	Experienced
																</MenuItem>
															</Select>
															<FormHelperText
																error={
																	!!meta.touched &&
																	!!(Array.isArray(
																		meta.error,
																	) &&
																	meta.error
																		.length >=
																		index
																		? meta
																				.error[
																				index
																			]
																				?.jobCategoryExperience
																		: null)
																}
															>
																{!!meta.touched &&
																!!(
																	Array.isArray(
																		meta.error,
																	) &&
																	meta.error
																		.length >=
																		index &&
																	meta.error[
																		index
																	]
																		?.jobCategoryExperience
																) ? (
																	meta.error[
																		index
																	]
																		?.jobCategoryExperience
																) : (
																	<span>
																		&nbsp;
																	</span>
																)}
															</FormHelperText>
														</FormControl>
													</TableCell>
												)}
												{showWave && (
													<TableCell
														sx={{
															width: '20%',
														}}
													>
														<FormControl
															variant='filled'
															fullWidth
														>
															<Select
																variant='standard'
																size={'small'}
																label=''
																disabled={
																	isInactive
																}
																onChange={e => {
																	const newValues =
																		{
																			...row,
																			wave: e
																				.target
																				.value,
																		};
																	replace(
																		index,
																		newValues,
																	);
																}}
																SelectDisplayProps={{
																	sx: {
																		padding:
																			'2px 0 3px 0',
																	},
																}}
																value={row.wave}
																onBlur={() =>
																	helpers.setTouched(
																		true,
																	)
																}
															>
																<MenuItem
																	value={1}
																>
																	1
																</MenuItem>
																<MenuItem
																	value={2}
																>
																	2
																</MenuItem>
																<MenuItem
																	value={3}
																>
																	3
																</MenuItem>
															</Select>
															<FormHelperText
																error={
																	!!meta.touched &&
																	!!(Array.isArray(
																		meta.error,
																	) &&
																	meta.error
																		.length >=
																		index
																		? meta
																				.error[
																				index
																			]
																				?.wave
																		: null)
																}
															>
																{!!meta.touched &&
																!!(
																	Array.isArray(
																		meta.error,
																	) &&
																	meta.error
																		.length >=
																		index &&
																	meta.error[
																		index
																	]?.wave
																) ? (
																	meta.error[
																		index
																	]?.wave
																) : (
																	<span>
																		&nbsp;
																	</span>
																)}
															</FormHelperText>
														</FormControl>
													</TableCell>
												)}
												<TableCell padding='checkbox'>
													<Box
														sx={{
															display: 'flex',
															justifyContent:
																'center',
															alignItems:
																'center',
															minHeight: 40,
														}}
													>
														{isInactive ? (
															<Tooltip
																title='This inactive job category is locked and cannot be edited.'
																placement='left'
															>
																<IconButton
																	aria-label='Help'
																	size='small'
																	onClick={() =>
																		onHelp(
																			row.jobCategoryId,
																		)
																	}
																>
																	<InfoIcon fontSize='small' />
																</IconButton>
															</Tooltip>
														) : (
															<Tooltip
																title='Click to remove this job category from your selections'
																placement='left'
															>
																<IconButton
																	aria-label='remove'
																	size='small'
																	onClick={() => {
																		remove(
																			index,
																		);
																		notifyJobCategoriesChange(
																			{
																				previousJobCategory:
																					row,
																			},
																		);
																	}}
																>
																	<DeleteIcon fontSize='small' />
																</IconButton>
															</Tooltip>
														)}
													</Box>
												</TableCell>
											</TableRow>
										);
									})}

								<TableRow
									style={{
										height: 33,
									}}
								>
									<TableCell
										sx={{ borderBottom: 'none' }}
										colSpan={headCells?.length}
									>
										{visibleRows.length < MAX_COUNT && (
											<Button
												sx={{ margin: '20px 0 -4px 0' }}
												variant='contained'
												color='success'
												startIcon={<AddIcon />}
												onClick={() => {
													const newJobCategory = {
														jobCategoryId: nanoid(),
														jobCategoryName: null,
														jobCategoryType: null,
														jobCategoryExperience:
															'New',
														active: true,
														wave: null,
													};
													helpers.setTouched(true);
													push(newJobCategory);
													notifyJobCategoriesChange({
														jobCategory:
															newJobCategory,
													});
												}}
											>
												Add Job Category
											</Button>
										)}
									</TableCell>
								</TableRow>
							</TableBody>
						)}
					</FieldArray>
				</Table>
			</TableContainer>
		</AppWrappedInput>
	);
};
export default JobCategoryTable;
